import { useMemo, useState, type CSSProperties } from 'react';
import {
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  RobotStatus,
  ROBOT_STATUS_LABELS,
  LocationCategory,
  UserRole,
  formatRobotShortId,
  ROBOT_LIST_SNAPSHOT_SOURCE_LABELS,
  isKstLastDayOfMonth,
  isRealYmd,
  kstDateTime,
  type RobotListSnapshotDto,
  type RobotSummaryDto,
} from '@bhops/shared';
import { canEditMfg } from '@/permissions';
// 계약 사실화 1단계 정정(26-07-28) — '구분' 칸 표기는 정본 한 벌에서만 온다.
// 정정9(26-07-29) — 오너 지시로 이 화면의 '구분'은 **구독/판매 두 단어**다. 그래서
// 원문 7값 표기 세트(formatContractReason · CONTRACT_REASON_OPTION_LABEL)를 더 부르지
// 않고, 정본이 정한 두 단어만 가져온다. 값 없음 표기(en-dash)도 정본 것을 쓴다.
import {
  CONTRACT_KIND_SALE,
  CONTRACT_KIND_SUBSCRIPTION,
  CONTRACT_REASON_EMPTY,
} from '@/lib/contract-reason';
import {
  robotsApi,
  robotSnapshotsApi,
  type ListRobotsQuery,
  type RobotListColumn,
  type RobotListSort,
} from '@/api/robots';
import { SortableTh, columnSortDir } from '@/components/SortableTh';
import { nextColumnSort, type ColumnSortKind, type ColumnSortState } from '@/lib/column-sort';
import { modelsApi } from '@/api/models';
import { extractApiError } from '@/api/client';
import { RobotStatusPill, LocationPill } from '@/components/StatusPill';
import { NewBadge } from '@/components/NewBadge';
import { Tooltip } from '@/components/Tooltip';
import { EditableSelect } from '@/components/EditableSelect';
import { Button } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { RobotCreateModal } from './robots/RobotCreateModal';
import { Modal } from '@/components/Modal';
import { RobotEditModal } from './robots/RobotEditModal';
import {
  ROBOT_LIFECYCLE_STAGES,
  StatusBar,
  robotStatusToStage,
  type RobotLifecycleStage,
} from '@/components/StatusBar';
import { LifecyclePipeline, type LifecycleStageData } from '@/components/LifecyclePipeline';
import { useDebounce } from '@/hooks/useDebounce';
import { Pagination } from '@/components/Pagination';
import { TableSkeleton } from '@/components/TableSkeleton';

// design_handoff_kuun_ux/README.md §② Phase 2 — Robots list refactor.
//
// 변경점 (existing 테스트 호환 유지):
//   1. 상단에 LifecyclePipeline 추가 — 6 단계 카드 클릭 시 status filter 토글.
//      카운트는 robotsApi.list({status: X, pageSize: 1}) 6 회 병렬 호출의 total 합.
//      (전용 endpoint 가 아직 없어 stop-gap. plan §②의 lifecycleCounts() 미존재 → 추후 1 endpoint로 치환 가능.)
//   2. 행 좌측에 3px <StatusBar /> 컬럼 추가 (헤더 라벨 없음 → 기존 columnheader assertion 영향 없음).
//   3. 인라인 액션 컬럼 (행 끝) — 편집(round5 #7c, eye 대체) / qr (QR 탭 직행).
//   4. 기존 SN/모델/상태/위치/회계번호/현재매장/검증 컬럼 + 페이지네이션 + CSV + 새 로봇 모달은 유지.

// PDF §Phase A 페이지 3 §2 / FB2 §1 (결정 F) — '전체 상태' dropdown 라벨은
// 라이프사이클 카드 라벨과 일치해야 한다. 라벨 표류를 막기 위해 공유 정전
// 소스 ROBOT_STATUS_LABELS(@bhops/shared)를 단일 출처로 소비한다.
// FB2 §1: 기존엔 INSTALL_PENDING(설치 대기)·IN_STORAGE(재고)가 누락되어
// 운영자가 해당 상태로 필터링할 수 없었음 → 라이프사이클 순서로 추가.
//
// round5 #20 에서 IN_STORAGE('재고')를 **다시 뺐다** — 그때는 "회수는 로봇을
// 출고 대기로 보내니 수동 전이로만 닿는 미사용 상태이고 운영 실재 0대" 였다.
//
// ⚠ 2026-09-18 되돌린다. 그 판단은 그때는 맞았지만, **7월 정정 때 연구소 로봇
//   여러 대가 재고로 옮겨졌고 아무도 이 목록을 되돌려 놓지 않았다.** 그래서
//   재고 로봇은 ① 골라 볼 수 없고 ② 아래 카드 숫자에서도 통째로 빠졌다
//   (카드 숫자를 이 목록을 따라가며 세기 때문이다 — stageQueriesConfig 주석 참고).
//
// ⚠⚠ **이 목록은 상태 전수여야 한다.** 여기서 한 줄을 빼면 그 상태의 로봇은
//    화면 어디에서도 안 세어진다. "안 쓰는 상태라 목록을 줄이자" 는 판단을
//    다시 하고 싶으면, 세는 경로부터 이 목록에서 떼어 놓고 할 것.
const STATUS_OPTIONS: Array<{ value: RobotStatus | ''; label: string }> = [
  { value: '', label: '전체 상태' },
  { value: RobotStatus.IN_PRODUCTION, label: ROBOT_STATUS_LABELS.IN_PRODUCTION },
  { value: RobotStatus.READY_TO_SHIP, label: ROBOT_STATUS_LABELS.READY_TO_SHIP },
  { value: RobotStatus.INSTALL_PENDING, label: ROBOT_STATUS_LABELS.INSTALL_PENDING },
  { value: RobotStatus.DEPLOYED, label: ROBOT_STATUS_LABELS.DEPLOYED },
  // 오너 결정 2026-09-18 — 행사·인증·테스트·사내로 나가 있는 로봇.
  { value: RobotStatus.OTHER, label: ROBOT_STATUS_LABELS.OTHER },
  { value: RobotStatus.IN_AS, label: ROBOT_STATUS_LABELS.IN_AS },
  { value: RobotStatus.IN_STORAGE, label: ROBOT_STATUS_LABELS.IN_STORAGE },
  // round10 요구2 — 폐기 예정/완료 분리.
  { value: RobotStatus.RETIREMENT_PLANNED, label: ROBOT_STATUS_LABELS.RETIREMENT_PLANNED },
  { value: RobotStatus.RETIRED, label: ROBOT_STATUS_LABELS.RETIRED },
];

// PDF §Phase A 페이지 3 §3 — '전체 위치' 옵션 5개: 연구소/공장/창고/
// 고객 현장/폐기. AS_CENTER 는 운영 룰 상 dropdown 노출 X (legacy
// 데이터는 LocationPill 라벨로 그대로 표시).
const LOCATION_OPTIONS: Array<{ value: LocationCategory | ''; label: string }> = [
  { value: '', label: '전체 위치' },
  { value: LocationCategory.RESEARCH, label: '연구소' },
  { value: LocationCategory.FACTORY, label: '공장' },
  { value: LocationCategory.WAREHOUSE, label: '창고' },
  { value: LocationCategory.CUSTOMER_SITE, label: '고객 현장' },
  { value: LocationCategory.DISPOSAL, label: '폐기' },
];

// 계약 사실화 1단계 정정6(26-07-29) — '구분' 필터(데이터 정리 스윕의 진입점).
//
// 계획 §1 은 "'확인 필요' 필터를 진입점으로 영업이 훑는다"를 잘못 저장된 판매/구독을
// 정리하는 방법으로 적었는데, 이 목록에는 그 필터가 없었다 — '구분' 칸은 배지만 그리고
// 그 행만 모아 볼 수단이 없었다. 이 select 가 그 자리다.
//
// ⚠ '확인 필요'는 오염 건을 다 가리키지 않는다 — 줄이 갈리지 않고 한 값으로 잘못 저장된
// 건(판매인데 구독으로 저장)은 판정 불가가 아니라 '구독'으로 자신 있게 보인다.
// 그 건은 '구독'으로 훑거나 고객사 목록의 '계약 형태' 칸에서 찾는다.
//
// ────────────────────────────────────────────────────────────────────────
// 정정9(26-07-29) — 오너 지시: "구분은 구독과 판매로만 표시되면 됩니다."
//
// 종전 선택지는 '확인 필요' + **원문 7값**(파일럿 · 시연 · 행사 · 사내 · 구독(운용리스)
// · 구독(금융리스) · 판매)이었다. 그 단어들이 '구분' 칸에서 사라졌으므로 필터에서도
// 걷어낸다 — 고를 수는 있는데 그 이름이 어느 칸에도 안 보이면 **필터가 화면을 반증한다**.
//
// 그리고 종전 코드는 이 값을 `contractReason` 이라는 이름으로 보내고 있었는데 서버 dto 는
// 이 라운드에 `contractKind` 로 개명됐다. 전역 ValidationPipe 가
// forbidNonWhitelisted 라 **폐기된 이름을 보내면 400**('property contractReason should
// not exist') — 즉 운영자가 구분 필터를 고르는 순간 목록이 오류로 비었다(실측:
// robots.controller.spec '구분 질의 파라미터 화이트리스트'). 이름도 함께 맞춘다.
//
// 값 집합은 서버 dto 의 ROBOT_CONTRACT_KIND_FILTERS 와 같다(SALE / SUBSCRIPTION /
// AMBIGUOUS). 사람이 읽는 단어는 표기 정본(lib/contract-reason)이 정한다 — 서버는
// 가름만 내려 주고 한국어를 만들지 않는다.
const CONTRACT_KIND_AMBIGUOUS = 'AMBIGUOUS';
/** 판정 불가 표기 — 필터 선택지와 칸의 배지가 **같은 단어**여야 한다(둘이 갈리면 좁힌 목록의 배지를 알아볼 수 없다). */
const CONTRACT_KIND_AMBIGUOUS_LABEL = '확인 필요';
const CONTRACT_KIND_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: '전체 구분' },
  // 오너가 말한 순서('구독과 판매').
  { value: 'SUBSCRIPTION', label: CONTRACT_KIND_SUBSCRIPTION },
  { value: 'SALE', label: CONTRACT_KIND_SALE },
  { value: CONTRACT_KIND_AMBIGUOUS, label: CONTRACT_KIND_AMBIGUOUS_LABEL },
];

/**
 * 계약 사실화 1단계 정정6(26-07-29) — 이 화면이 서버로 보내는 질의.
 *
 * 서버 ListRobotsQuery 에는 contractKind 가 있으나(apps/api dto), 공유 API 타입
 * (api/robots.ts ListRobotsQuery)은 이번 정정의 수정 허용 범위 밖이라 여기서 넓힌다.
 * 값 집합은 서버 dto 의 ROBOT_CONTRACT_KIND_FILTERS 와 같다 — 정렬 옵션
 * (ROBOT_LIST_SORTS ↔ RobotListSort)과 같은 관례로 어휘를 양쪽에 적는다.
 * **후속** — 공유 API 타입(api/robots.ts ListRobotsQuery)에 contractKind 를,
 * 공유 DTO(packages/shared api-contracts RobotSummaryDto)에 contractKind 를 올리고
 * 이 교차 타입 두 개를 지울 것. 두 파일 모두 이번 차수의 수정 허용 범위 밖이라 남겼다.
 */
type RobotsListFilters = ListRobotsQuery & { contractKind?: string };

/**
 * 머리를 눌러 정렬(2026-10-01 오너 요청 — 고객사 마스터와 같은 방식). 정렬은 **서버**가 한다
 * (로봇은 페이지로 나눠 받으므로 화면에서 한 페이지만 줄 세우면 틀린다). 칸별 단계:
 * 글자 = 가나다순 → 역순 → 해제, 날짜(첫 제조·출고 월) = 최신 → 과거 → 해제,
 * 분류(상태·위치) = 분류별 → 해제. 출고일정은 위 정렬 선택(예정일·출고월 ↑↓)이 맡고,
 * 구분·비고는 정렬하지 않는다.
 */
const ROBOT_COLUMN_KIND: Record<RobotListColumn, ColumnSortKind> = {
  sn: 'text',
  model: 'text',
  color: 'text',
  variant: 'text',
  status: 'category',
  location: 'category',
  firstProductionYm: 'date',
  firstShipmentYm: 'date',
  customer: 'text',
  accountingBookNo: 'text',
};

/**
 * 정정9(26-07-29) — 서버가 내려 주는 **접힌 '구분'** 토큰.
 *
 * 'AMBIGUOUS' 는 "접어도 판매/구독이 갈린다"(화면의 '확인 필요')이고, null 은 미배정이다.
 * 필터가 보내는 값과 **같은 어휘**라 "구독으로 좁혔는데 판매라고 적힌 행"이 원리적으로
 * 나올 수 없다(같은 서버 함수 deriveContractKind 의 결과를 필터·칸·CSV 가 함께 쓴다).
 */
type RobotContractKind = 'SALE' | 'SUBSCRIPTION' | typeof CONTRACT_KIND_AMBIGUOUS;
type RobotRow = RobotSummaryDto & { contractKind?: RobotContractKind | null };

const PAGE_SIZE = 50;

// S2 Phase 2.7 — 색상 facet 옵션. OptionGroup 'robot.color' 는 EditableSelect
// 로 picklist 를 직접 fetch 한다.
// FB3 §4 — 불감지(robot.sensorType) facet 은 운영에서 미사용이라 제거.
// API 의 sensorType 파라미터 plumbing 은 무해하게 유지(UI 에서 set 안 함).
const COLOR_GROUP_KEY = 'robot.color';

// S2 Phase 2.7 — 정렬 select 옵션 → api ListRobotsQuery.sort 매핑.
// 개선계획 §4 (결정 ⑤B) — '출고일정 ↑↓' 는 무엇으로 줄 세우는지가 모호했다.
// 이미 나간 기록(첫 출고월)과 앞으로 나갈 날(설치 예정일)을 라벨로 갈라 놓는다.
const SORT_OPTIONS: Array<{ value: RobotListSort; label: string }> = [
  { value: 'created_desc', label: '최신 등록순' },
  { value: 'shipment_scheduled_asc', label: '출고 예정일 순 ↑' },
  { value: 'shipment_scheduled_desc', label: '출고 예정일 순 ↓' },
  { value: 'shipment_asc', label: '첫 출고월 순 ↑' },
  { value: 'shipment_desc', label: '첫 출고월 순 ↓' },
  { value: 'model', label: '모델순' },
  { value: 'status', label: '상태순' },
];

// 추가수정(26-07-27) §3 (결정 ①B) — 출고일정 컬럼 범례. 배지 글자가 사라지고
// 색상으로만 구분하므로 범례가 색의 뜻을 설명한다.
const SHIPMENT_LEGEND = '파란색은 앞으로 나갈 설치 예정일이고, 회색은 이미 나간 실제 출고일입니다.';

/**
 * S2 Phase 2.7 — YYMM 문자열(예: "2604" = 2026년 4월) 표시. "YY/MM" 로
 * 보여주고, 미상/빈 값은 em-dash. 4자리 숫자가 아니면 (legacy 이형 데이터)
 * 원문을 그대로 노출한다.
 * 추가수정(26-07-27) §3 — 출고일정 셀의 첫 출고월 폴백이 삭제되어 이제
 * '첫 제조 월'·'첫 출고 월' 칸만 쓴다.
 */
function formatShipmentYm(ym: string | null | undefined): string {
  if (!ym) return '—';
  return /^\d{4}$/.test(ym) ? `${ym.slice(0, 2)}/${ym.slice(2)}` : ym;
}

/** ISO 날짜 → "YY.MM.DD". 파싱 불가면 null. */
function formatYmd(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}.${mm}.${dd}`;
}

/**
 * 추가수정(26-07-27) §3 (결정 ①B) — 출고일정 셀. 뜻은 하나 — "이 로봇이 지금
 * 고객사 기준으로 언제 나가나/나갔나":
 *   ① 고객사에 나가 있음(currentDeployedAt 있음) → 실적(중립색). 계약에 설치
 *      예정일이 연결돼 있어도 실제로 나간 날이 우선 — 이미 나간 로봇이 파란
 *      '예정'으로 보이던 현상이 이번에 없애는 바로 그것.
 *   ② 나간 기록은 없는데 매장이 붙어 있고(currentStore) 비고객 반출도 아님
 *      → em-dash 고정. 이것이 '고아 DEPLOYED'(활성 배치 없이 상태만 올라간
 *      과거 데이터)라, 낡은 예약의 예정일로 강하시키면 안 된다.
 *   ③ 그 외 설치 예정일(shipmentScheduledAt) 있음 → 예정(파랑). 재고 예약분과,
 *      **행사·전시 반출 중이면서 계약에 예약된 로봇**(currentStore 는 붙지만
 *      hasActiveNonCustomerDeployment=true)이 둘 다 여기로 온다 — 곧 나갈
 *      로봇이므로 예정일을 보여주는 것이 맞다.
 *   ④ 그 외(재고·정비 등) em-dash. firstShipmentYm 폴백은 삭제 — 그 값은
 *      바로 옆 '첫 출고 월' 칸 전담(한 값을 두 칸이 반복하지 않는다).
 */
function ShipmentScheduleCell({ robot }: { robot: RobotSummaryDto }) {
  const deployed = formatYmd(robot.currentDeployedAt);
  if (deployed) {
    return <ShipmentMark kind="실적" value={deployed} />;
  }
  // ② 고아 DEPLOYED 방어 — 매장은 붙어 있는데 활성 배치가 하나도 없는 로봇.
  if (robot.currentStore && !robot.hasActiveNonCustomerDeployment) {
    return <span style={{ color: 'var(--fg-subtle)' }}>—</span>;
  }
  const scheduled = formatYmd(robot.shipmentScheduledAt);
  if (scheduled) {
    return <ShipmentMark kind="예정" value={scheduled} />;
  }
  return <span style={{ color: 'var(--fg-subtle)' }}>—</span>;
}

// 추가수정(26-07-27) §3 — 배지(pill) 글자를 없애고 색 텍스트만 남긴다.
// 파랑 = 예정 / 회색 = 실적(범례·확정 계획서 §3 '중립(회색 계열)' 과 일치 —
// 본문 검정(--fg-default)이면 다른 셀 텍스트와 구분이 안 된다).
// 추가수정(26-07-27) 감사 지적 B — native title 은 키보드·스크린리더에 안 뜨므로
// 리포 공용 Tooltip(호버/포커스 말풍선)으로 교체하고, 뜻+날짜를 담은 aria-label
// 을 병기한다. testid 는 날짜 텍스트 span 에 유지.
function ShipmentMark({ kind, value }: { kind: '예정' | '실적'; value: string }) {
  const planned = kind === '예정';
  const meaning = planned ? '필드팀이 정한 설치 예정일' : '실제로 출고한 날';
  return (
    // 추가수정(26-07-27) — focusable={false}: 목록 50행 전부에 tabIndex 를 주면
    // 탭 순서가 오염된다(ContractCard 의 비포커스 사용 패턴을 따름). 키보드
    // 사용자 몫은 aria-label(뜻+날짜)이 감당하고, 말풍선은 호버로 뜬다.
    <Tooltip content={meaning} focusable={false}>
      <span
        data-testid={planned ? 'shipment-mark-planned' : 'shipment-mark-actual'}
        aria-label={`${meaning} ${value}`}
        style={{ color: planned ? 'var(--robot-shipping)' : 'var(--fg-muted)' }}
      >
        {value}
      </span>
    </Tooltip>
  );
}

// round10 (요구 4) — 구독(렌탈)/판매 구분. 예약계약 라인 reason(DeploymentReason
// 7값)에서 파생. 미배정(reservedContract 없음) 로봇은 null → 줄표.
//
// 계약 사실화 1단계 정정4(26-07-29) — 여기 있던 "고객사 마스터는 SALE/
// LEASE_OPERATING 2값만 생성한다"는 사실이 아니게 됐다. 고객사 마스터 모달의 줄별
// '계약 형태' 칸이 원문 7값을 그대로 보내므로(정정 26-07-28), 이 칸에는 '판매' ·
// '구독' 외에 '구독(금융리스)' · '파일럿' · '시연' · '행사' · '사내'도 실제로 뜬다.
// 그 문구는 표기 정본(lib/contract-reason)이 정한다.
//
// 판매구분 정정(26-07-28) — 서버가 계약 첫 줄이 아니라 **로봇 모델과 일치하는
// 라인**의 reason 을 내려주게 바뀌었다(혼합 계약에서 전 로봇이 한쪽으로
// 뭉뚱그려지던 결함 정정). 판정 불가(contractReasonAmbiguous)는 아래
// ContractReasonCell 이 '확인 필요' 로 처리한다.
//
// 계약 사실화 1단계 정정(26-07-28) — 여기 있던 로컬 formatContractReason
// ("SALE 이면 판매, 나머지 전부 구독")을 지우고 표기 정본(lib/contract-reason)을
// 부른다. 옛 규칙은 파일럿·시연·행사·사내 이동 줄까지 '구독'이라고 말해서 계약
// 상세 화면과 서로를 반증했다. SALE='판매' / LEASE_OPERATING='구독' 은 그대로이고,
// 나머지 5값이 제 이름으로 바뀐다(LEASE_FINANCE='구독(금융리스)' 포함).

// 판매구분 정정(26-07-28) — '구분' 셀. 예약 계약에서 이 로봇에 해당할 수 있는
// 라인들이 갈리면(예: 같은 모델의 판매 라인 + 구독 라인) 서버가 단정을 포기한다.
// 화면도 '판매/구독' 중 하나를 찍는 대신 '확인 필요'(주황)를 보여 준다 — 조용히
// 틀린 값보다 낫다.
// 표기 관례: 값 없음은 줄표(–), 설명이 필요한 표식은 리포 공용 Tooltip — 출고일정
// 셀(ShipmentMark)과 동일하게 focusable={false} 로 목록 행마다 탭 정지점이 생기지
// 않게 하고, 키보드·스크린리더 몫은 aria-label 이 감당한다.
//
// ────────────────────────────────────────────────────────────────────────
// 정정9(26-07-29) — 오너 지시로 이 칸은 **구독 / 판매 두 단어**만 찍는다. 파일럿 ·
// 시연 · 행사 · 사내 · 금융리스는 모두 '구독'이다.
//
// 판정을 화면이 다시 하지 않고 **서버가 접어 내려 준 값**(contractKind)을 읽는다.
// 근거 두 가지:
//   ① 화면은 접은 뒤의 '확인 필요'를 재현할 수 없다. 원문이 갈리면 서버는
//      contractReason=null + contractReasonAmbiguous=true 를 내리는데, 그 두 값만으로는
//      "접으면 둘 다 구독이라 갈리지 않는다"(파일럿 줄 + 구독 줄)를 알 방법이 없다 —
//      계약 줄이 DTO 에 없다. 원문 기준 배지를 그대로 두면 확인할 것이 없는 로봇이
//      스윕 목록에 계속 섞인다.
//   ② '구분' 필터가 이미 서버에서 같은 판정을 한다(robots.service contractKindMatchIds
//      → deriveContractKind). 화면이 따로 판정하면 규칙이 두 벌이 되어 **구독으로 좁힌
//      목록에 '판매'라고 적힌 행**이 섞일 수 있다. 필터·칸·CSV 가 한 판정을 함께 쓴다.
// 사람이 읽는 단어는 여전히 표기 정본(lib/contract-reason)의 것이다.
//
// data-testid 는 종전 이름을 유지한다 — 뜻이 바뀌지 않았고(판정 불가 표식), 이름을
// 갈면 이 표식을 보는 기존 시험이 조용히 아무것도 못 찾는 상태가 된다.
function ContractKindCell({ robot }: { robot: RobotRow }) {
  const kind = robot.contractKind ?? null;
  if (kind === CONTRACT_KIND_AMBIGUOUS) {
    const meaning =
      '예약 계약에 판매 라인과 구독 라인이 섞여 있어 이 로봇의 구분을 확정할 수 없습니다. 계약 상세에서 라인 구성을 확인해 주세요.';
    return (
      <Tooltip content={meaning} focusable={false}>
        <span
          data-testid="contract-reason-ambiguous"
          aria-label={`구분 ${CONTRACT_KIND_AMBIGUOUS_LABEL}. ${meaning}`}
          style={{ color: 'var(--fg-warning)' }}
        >
          {CONTRACT_KIND_AMBIGUOUS_LABEL}
        </span>
      </Tooltip>
    );
  }
  // 구분 수동 정정(plan 2-5) — 수동으로 지정된 값은 배지로 구별한다. 자동값과
  // 어긋나 있어도 조용히 같아 보이면 정정이 숨는다.
  const manualBadge =
    robot.contractKindSource === 'MANUAL' ? (
      <span
        data-testid="contract-kind-manual"
        style={{ marginLeft: 6, fontSize: 11, color: 'var(--fg-warning)' }}
      >
        수동
      </span>
    ) : null;
  if (kind === 'SALE')
    return (
      <>
        {CONTRACT_KIND_SALE}
        {manualBadge}
      </>
    );
  if (kind === 'SUBSCRIPTION')
    return (
      <>
        {CONTRACT_KIND_SUBSCRIPTION}
        {manualBadge}
      </>
    );
  // 미배정(예약 계약 없음) — 그리고 서버가 아직 이 값을 안 내려 주는 경우도 여기로
  // 온다. 원문 contractReason 을 대신 찍어 메꾸지 않는다: 그러면 이 칸에 '파일럿' 같은
  // 걷어낸 단어가 다시 뜨고, 필터가 잡지 못하는 행에 값이 적혀 둘이 어긋난다.
  return <>{CONTRACT_REASON_EMPTY}</>;
}

/**
 * S2 Phase 2.7 — 구분 사양 표시. 모델에 축 라벨(variantAxisLabel)이 있으면
 * "라벨: 값" (예: "방향: Right"), 없으면 값 단독. 값도 없으면 em-dash.
 */
function formatVariant(
  axisLabel: string | null | undefined,
  value: string | null | undefined,
): string {
  const v = value && value.trim() ? value : null;
  if (axisLabel && axisLabel.trim()) return `${axisLabel}: ${v ?? '—'}`;
  return v ?? '—';
}

// round8 (요구 4) — 클릭→필터 셀 버튼 스타일. 활성(현재 필터값)이면 브랜드색
// 강조 + 테두리. 셀 텍스트처럼 보이되 커서·tooltip 으로 클릭 가능함을 알린다.
function filterCellStyle(active: boolean): CSSProperties {
  return {
    font: 'inherit',
    color: active ? 'var(--brand)' : 'inherit',
    background: active ? 'var(--bg-sunken)' : 'transparent',
    border: `1px solid ${active ? 'var(--brand)' : 'transparent'}`,
    borderRadius: 6,
    padding: '1px 6px',
    margin: '-1px -2px',
    cursor: 'pointer',
    textAlign: 'left',
    maxWidth: '100%',
  };
}

/**
 * Lifecycle pipeline 의 카드별 backend RobotStatus 집계 매핑.
 *
 * 2026-09-18 — **카드 한 장 = 상태 하나**로 맞췄다. 종전엔 stock 한 장이
 * 제조 중과 재고 둘을 묶고 있었는데, 카드를 눌렀을 때는 첫 상태(제조 중)로만
 * 걸러져 "숫자는 40인데 눌러 보면 25" 가 됐다.
 *
 * ⚠ 여기 적힌 상태 전부가 `STATUS_OPTIONS` 에도 있어야 한다 — 숫자를 세는
 *   조회가 그 목록을 따라 돌기 때문이다. 아래 STAGE_STATUS_ORDER 가 그 둘이
 *   어긋나지 않게 잇는다.
 */
const STAGE_TO_STATUSES: Record<RobotLifecycleStage, RobotStatus[]> = {
  stock: [RobotStatus.IN_PRODUCTION],
  shipping: [RobotStatus.READY_TO_SHIP],
  // FB2 §1 — INSTALL_PENDING enum 이 존재하므로 '설치 대기' 카드가 실제
  // 카운트를 표시한다(이전 빈 배열 → 항상 0 버그). 카드 클릭 시 해당 status
  // 로 필터링도 활성화됨(handleStageClick).
  installing: [RobotStatus.INSTALL_PENDING],
  operating: [RobotStatus.DEPLOYED],
  // 오너 결정 2026-09-18 — 행사·인증·테스트·사내.
  other: [RobotStatus.OTHER],
  maintenance: [RobotStatus.IN_AS],
  // 2026-09-18 — 재고가 제 카드를 갖는다(종전엔 stock 에 묶여 안 세어졌다).
  storage: [RobotStatus.IN_STORAGE],
  // round10 요구2 — 폐기 예정/완료 별도 버킷.
  retirePlanned: [RobotStatus.RETIREMENT_PLANNED],
  retired: [RobotStatus.RETIRED],
};

// 순서·전수는 정본 한 벌(StatusBar 의 ROBOT_LIFECYCLE_STAGES) — 로봇 상세의 단계 표시와
// 같은 목록이어야 한다(2026-09-23, 두 곳이 각자 적어 두다 갈렸다).
const LIFECYCLE_STAGES: readonly RobotLifecycleStage[] = ROBOT_LIFECYCLE_STAGES;

/**
 * 카드가 실제로 더하는 상태 전수 — **숫자를 세러 갈 목록**.
 *
 * ⚠ 종전엔 이 목록이 아니라 `STATUS_OPTIONS`(필터 드롭다운)를 따라 돌았다.
 *   그래서 드롭다운에서 빠진 상태(재고)는 조회 자체가 안 나갔고, 카드가 그
 *   상태를 더하도록 적혀 있어도 **늘 0 을 더했다** — 띠 합이 전체 로봇 수보다
 *   조용히 적었다. 세는 목록은 화면에 무엇을 보여 줄지(드롭다운)가 아니라
 *   **카드가 무엇을 더하는지**(STAGE_TO_STATUSES)에서 나와야 한다.
 */
const STAGE_STATUS_ORDER: RobotStatus[] = LIFECYCLE_STAGES.flatMap((s) => STAGE_TO_STATUSES[s]);

// ─── 기준일 마감본(plan robot-asof-snapshot-2026-10) ─────────────────────────

/**
 * 마감본이 대표하는 시각의 사람 표기. 말일 자동 마감·백업 복원은 그날 23:59, 관리자 '지금 마감'은 찍은
 * 시각이다(낮에 찍은 것을 '23:59 기준'이라 적으면 거짓이다).
 */
export function robotAsOfLabel(
  s: Pick<RobotListSnapshotDto, 'basisDate' | 'cutoffAt' | 'source'>,
): string {
  if (s.source === 'MANUAL') return kstDateTime(s.cutoffAt) ?? s.basisDate;
  const [y, m, d] = s.basisDate.split('-').map(Number);
  return `${y}년 ${m}월 ${d}일 23:59`;
}

function asOfOptionLabel(s: RobotListSnapshotDto): string {
  return s.source === 'MANUAL'
    ? `${kstDateTime(s.cutoffAt) ?? s.basisDate} 지금 마감`
    : `${s.basisDate} 마감`;
}

export function RobotsListPage() {
  // A3 — honor a ?status=… deep-link from the dashboard hero ("정비 중 로봇" → IN_AS).
  // The dashboard links to the canonical /manufacturing/robots path so the query survives
  // (the bare /robots route redirects and would otherwise drop it).
  const [searchParams, setSearchParams] = useSearchParams();
  // 기준일 마감본 — 주소의 ?asOf= 가 정본(링크로 열면 같은 기준일로 열린다). 형식이 틀리면 '지금'.
  const asOfParam = searchParams.get('asOf');
  const asOf = asOfParam && isRealYmd(asOfParam) ? asOfParam : undefined;
  const [filters, setFilters] = useState<RobotsListFilters>(() => {
    const status = searchParams.get('status') as ListRobotsQuery['status'];
    return { page: 1, pageSize: PAGE_SIZE, ...(status ? { status } : {}) };
  });
  const [createOpen, setCreateOpen] = useState(false);
  // 2026-10-21 오너 — 비고는 한 줄로 줄이고('…'), 누르면 적어 둔 내용 전체를 보여 준다.
  const [notesFor, setNotesFor] = useState<{ label: string; notes: string } | null>(null);
  // round5 #7c — 행 인라인 편집 대상. RobotEditModal 은 RobotSummaryDto 를
  // 받으므로 목록 행을 그대로 넘긴다.
  const [editTarget, setEditTarget] = useState<RobotSummaryDto | null>(null);
  const [exporting, setExporting] = useState(false);
  // docs/work_log — search debounced (300ms) so typing doesn't hit the
  // API on every keystroke. Effective query merges debounced search.
  const [searchInput, setSearchInput] = useState<string>('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const toast = useToast();
  const navigate = useNavigate();
  const userRole = useAuthStore((s) => s.user?.role);
  // round12 버그1 — 이 게이트는 phase-1 초기값(ADMIN/MANAGER)이 그대로 남아
  // 제조팀(MFG_MANAGER)에게 등록/편집 진입점이 아예 렌더되지 않았다. 서버는
  // 이미 MFG_MANAGER 의 Robot create/update 를 허용한다 → canEditMfg 로 통일.
  const canCreate = userRole ? canEditMfg(userRole) : false;
  // round11 요구3 — 회계번호 칸은 관리자 한정 표시.
  // ⚠️ 표시 게이팅일 뿐 보안 경계가 아니다: 서버 buildSummary 의 `{ ...robot }`
  // 전개 + RobotSummaryDto 선언 때문에 회계번호는 이미 VIEWER/MANAGER 응답
  // JSON 에도 들어 있어 devtools 로 그대로 보인다. 서버 게이팅은 이번 범위 밖
  // (별건 보안 부채).
  const isAdmin = userRole === UserRole.ADMIN;

  // round6 §1-8 — 복구 / 완전 삭제 뮤테이션.
  const qc = useQueryClient();
  const restoreM = useMutation({
    mutationFn: (id: string) => robotsApi.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robots'] });
      toast.success('로봇을 복구했습니다.', '복구');
    },
    onError: (err) => toast.error(extractApiError(err), '복구 실패'),
  });
  const permDeleteM = useMutation({
    mutationFn: ({ id, includeHistory }: { id: string; includeHistory?: boolean }) =>
      robotsApi.permanentDelete(id, includeHistory),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robots'] });
      toast.success('로봇을 완전 삭제했습니다.', '완전 삭제');
    },
    onError: (err, vars) => {
      const msg = extractApiError(err);
      // round7 §1-2 (D3) — 로봇 자체 이력만 걸린 400 이면 서버 메시지가
      // '이력 포함 삭제'를 안내한다 → 한 번 더 확인받고 이력 포함 재시도.
      // 고객사 소유 데이터(SO/인수증/서명)가 걸린 400 은 재시도 없이 그대로 노출.
      if (!vars.includeHistory && msg.includes('이력 포함 삭제')) {
        if (
          window.confirm(
            `${msg}\n\n출고, QR, 리포트, 방문, 이전, 펌웨어, ECN, 부품 이력까지 모두 함께 영구 삭제할까요?\n삭제 후에는 되돌릴 수 없습니다.`,
          )
        ) {
          permDeleteM.mutate({ id: vars.id, includeHistory: true });
          return;
        }
        return;
      }
      toast.error(msg, '완전 삭제 실패');
    },
  });

  // 기준일 고르기 목록 · 관리자 '지금 마감'.
  const snapshotsQuery = useQuery({
    queryKey: ['robot-snapshots'],
    queryFn: () => robotSnapshotsApi.list(),
    staleTime: 60_000,
  });
  const snapshots = snapshotsQuery.data ?? [];
  const captureM = useMutation({
    mutationFn: () => robotSnapshotsApi.capture(),
    onSuccess: (snap) => {
      qc.invalidateQueries({ queryKey: ['robot-snapshots'] });
      toast.success(`${robotAsOfLabel(snap)} 기준 마감본을 만들었습니다.`, '지금 마감');
    },
    onError: (err) => toast.error(extractApiError(err), '지금 마감 실패'),
  });
  // 말일 마감본은 23:59 자동 마감 몫이라 서버가 막는다 — 누르기 전에 버튼에서 말한다.
  const isLastDay = isKstLastDayOfMonth(Date.now());
  function selectAsOf(next: string | undefined): void {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (next) sp.set('asOf', next);
      else sp.delete('asOf');
      return sp;
    });
    setFilters((f) => ({ ...f, page: 1 }));
  }

  async function handleExport(): Promise<void> {
    setExporting(true);
    try {
      // Strip pagination from the query — export returns up to 10K rows.
      // Use the effective (debounced) filters so the CSV matches what
      // the operator sees on screen.
      const {
        page: _p,
        pageSize: _ps,
        ...rest
      } = {
        ...filters,
        search: debouncedSearch || undefined,
        // 기준일 마감본이면 그날 기준 CSV(회계용 칸 포함, 파일 이름에 기준일).
        ...(asOf ? { asOf } : {}),
      };
      await robotsApi.exportCsv(rest);
    } catch (err) {
      toast.error(extractApiError(err), 'CSV 내보내기 실패');
    } finally {
      setExporting(false);
    }
  }

  // Effective filters merge the debounced search so React Query's key
  // is stable until the debounce settles.
  const [colSort, setColSort] = useState<ColumnSortState<RobotListColumn> | null>(null);
  const effectiveFilters: RobotsListFilters = {
    ...filters,
    search: debouncedSearch || undefined,
    ...(asOf ? { asOf } : {}),
    ...(colSort
      ? {
          orderBy: colSort.key,
          orderDir: columnSortDir(ROBOT_COLUMN_KIND[colSort.key], colSort.step),
        }
      : {}),
  };
  function toggleColumnSort(key: RobotListColumn): void {
    setColSort((cur) => nextColumnSort(cur, key, ROBOT_COLUMN_KIND[key]));
    setFilters((f) => ({ ...f, page: 1 }));
  }
  const sortableTh = (key: RobotListColumn, label: string) => (
    <SortableTh
      label={label}
      kind={ROBOT_COLUMN_KIND[key]}
      step={colSort?.key === key ? colSort.step : null}
      onToggle={() => toggleColumnSort(key)}
      testId={`robots-sort-th-${key}`}
      thStyle={th}
    />
  );

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['robots', effectiveFilters],
    queryFn: () => robotsApi.list(effectiveFilters),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });

  // round8 (요구 3) — 모델별 필터 옵션 소스. /models 를 한 번 로드해 툴바 select 에
  // 사용한다. modelCode 필터 자체는 백엔드(list/export)까지 이미 연결돼 있어 UI
  // 컨트롤만 추가하면 된다. 값 = 각 모델의 code(exact-match).
  const modelsQuery = useQuery({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
  const modelOptions = modelsQuery.data?.items ?? [];

  // §② Phase 2 — Lifecycle pipeline 의 카드별 counts.
  //
  // ⚠ 2026-09-18 고침 — 종전엔 이 조회가 **STATUS_OPTIONS(필터 드롭다운)** 를
  //   따라 돌았다. 드롭다운에서 빠진 상태(재고)는 조회가 안 나가 `statusTotals`
  //   에 자리가 안 생겼고, 카드가 그 상태를 더하도록 적혀 있어도 `?? 0` 이 되어
  //   **늘 0 을 더했다** — 띠 합이 전체 로봇 수보다 조용히 적었다.
  //   이제 **카드가 실제로 더하는 상태**(STAGE_STATUS_ORDER)를 따라 돈다.
  //   향후 robotsApi.lifecycleCounts() (단일 endpoint) 로 치환 권장.
  //
  // docs/work_log — stage counts are global ("all robots in this stage"),
  // not scoped to the current text/status filter. Memoize so the queries
  // array keeps reference equality across re-renders (e.g. typing in the
  // search input shouldn't refetch stage counts), and disable refetch on
  // mount + window focus. 60s staleTime keeps the pipeline pleasantly
  // stable for an operator's session.
  const stageQueriesConfig = useMemo(
    () =>
      STAGE_STATUS_ORDER.map((status) => ({
        // 기준일 마감본이면 띠 숫자도 그날 기준(지금 숫자 위에 얼린 목록을 겹쳐 보이지 않게).
        queryKey: ['robots-count', status, asOf ?? 'now'] as const,
        queryFn: () => robotsApi.list({ status, pageSize: 1, page: 1, ...(asOf ? { asOf } : {}) }),
        staleTime: 60_000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      })),
    [asOf],
  );
  const stageQueries = useQueries({ queries: stageQueriesConfig });

  // 상태별 합계 — 카드가 더할 상태 전수를 같은 순서로 받는다.
  const statusTotals = useMemo(() => {
    const map = new Map<RobotStatus, number>();
    STAGE_STATUS_ORDER.forEach((status, idx) => {
      const q = stageQueries[idx];
      if (q?.data?.total != null) {
        map.set(status, q.data.total);
      }
    });
    return map;
  }, [stageQueries]);

  const lifecycleData = useMemo<LifecycleStageData[]>(() => {
    const activeStage = filters.status != null ? robotStatusToStage(filters.status) : undefined;
    return LIFECYCLE_STAGES.map((stage) => ({
      key: stage,
      count: STAGE_TO_STATUSES[stage].reduce((sum, s) => sum + (statusTotals.get(s) ?? 0), 0),
      active: stage === activeStage,
    }));
  }, [filters.status, statusTotals]);

  function handleStageClick(stage: RobotLifecycleStage): void {
    // Toggle: 활성 stage 를 다시 누르면 해제. 그 외엔 첫 매핑 status 로 설정.
    const currentlyActiveStage =
      filters.status != null ? robotStatusToStage(filters.status) : undefined;
    if (stage === currentlyActiveStage) {
      setFilters((f) => ({ ...f, status: undefined, page: 1 }));
      return;
    }
    const first = STAGE_TO_STATUSES[stage][0];
    if (first == null) {
      // installing stage — no backend enum, no-op.
      return;
    }
    setFilters((f) => ({ ...f, status: first, page: 1 }));
  }

  // 정정9(26-07-29) — 서버가 접힌 '구분'(contractKind)을 함께 내려 준다. 공유 DTO 에
  // 아직 그 칸이 없어(수정 허용 범위 밖) 이 화면에서 넓힌 모양으로 받는다(RobotRow 주석).
  const items: RobotRow[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? PAGE_SIZE;

  // 띠에 적을 마감본 머리 — 서버가 목록과 함께 준 것이 정본, 아직 안 왔으면 고르기 목록에서.
  // 기준일을 바꾸는 사이 이전 응답(keepPreviousData)의 머리가 남아 있을 수 있어 날짜가 같을 때만 쓴다.
  const asOfMeta = asOf
    ? data?.asOf?.basisDate === asOf
      ? data.asOf
      : snapshots.find((s) => s.basisDate === asOf)
    : undefined;
  // 주소의 기준일에 마감본이 없다(오래된 링크 · 잘못 친 날짜) — '기준 기록입니다'라고 말하지 않는다.
  const asOfStatus = (error as { response?: { status?: number } } | null)?.response?.status;
  const asOfMissing =
    !!asOf &&
    (asOfStatus === 404 ||
      asOfStatus === 400 ||
      (snapshotsQuery.isSuccess && !snapshots.some((s) => s.basisDate === asOf)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      {asOf && (
        <div role="status" data-testid="robots-asof-banner" style={asOfBanner}>
          {asOfMissing ? (
            <span data-testid="robots-asof-missing">
              <b>{asOf} 마감본이 없습니다.</b> 기준일을 다시 고르거나 지금으로 돌아가세요.
            </span>
          ) : (
            <span>
              <b>{asOfMeta ? robotAsOfLabel(asOfMeta) : asOf} 기준 기록입니다.</b> 이후에 고친
              내용은 반영되지 않습니다.
              {asOfMeta && <> 출처: {ROBOT_LIST_SNAPSHOT_SOURCE_LABELS[asOfMeta.source]}</>}
            </span>
          )}
          <button type="button" onClick={() => selectAsOf(undefined)} style={textActionBtn}>
            지금으로 돌아가기
          </button>
        </div>
      )}
      {/* §② — Lifecycle pipeline (클릭 = status filter 토글) */}
      {/* 2026-10-21 오너 — 상자 · 제목 없이 단계 칸만(기준일은 아래 '기준일' 고르기가 보여 준다). */}
      <LifecyclePipeline bare stages={lifecycleData} onStageClick={handleStageClick} />

      <div style={toolbar}>
        <input
          placeholder="SN이나 회계번호로 검색"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setFilters((f) => ({ ...f, page: 1 }));
          }}
          style={{ ...filterInput, width: 240 }}
        />
        <select
          aria-label="상태 필터"
          value={filters.status ?? ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              status: (e.target.value || undefined) as RobotStatus | undefined,
              page: 1,
            }))
          }
          style={filterInput}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="위치 필터"
          value={filters.locationCategory ?? ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              locationCategory: (e.target.value || undefined) as LocationCategory | undefined,
              page: 1,
            }))
          }
          style={filterInput}
        >
          {LOCATION_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {/* round8 (요구 3) — 모델별 필터 (modelCode exact-match, 백엔드 기존 지원). */}
        <select
          aria-label="모델 필터"
          value={filters.modelCode ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, modelCode: e.target.value || undefined, page: 1 }))
          }
          style={filterInput}
        >
          <option value="">전체 모델</option>
          {modelOptions.map((m) => (
            <option key={m.id} value={m.code}>
              {m.displayName}
            </option>
          ))}
        </select>
        {/* 계약 사실화 1단계 정정6(26-07-29) — '구분' 필터. 계획 §1 이 전제한 데이터
            정리 스윕의 진입점(그 전엔 '확인 필요' 행만 모아 볼 수단이 없었다).
            파생값 필터라 서버가 파생 후 id 집합으로 좁힌다 → 총계·페이지가 정확하다
            (robots.service.contractKindMatchIds).
            정정9(26-07-29) — 선택지가 구독/판매/확인 필요 3개이고, 보내는 이름은
            contractKind 다(폐기된 contractReason 은 서버가 400 으로 거부한다). */}
        <select
          aria-label="구분 필터"
          value={filters.contractKind ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, contractKind: e.target.value || undefined, page: 1 }))
          }
          style={filterInput}
        >
          {CONTRACT_KIND_FILTER_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {/* S2 Phase 2.7 — 색상 facet (OptionGroup robot.color). */}
        <EditableSelect
          groupKey={COLOR_GROUP_KEY}
          value={filters.color ?? ''}
          onChange={(v) => setFilters((f) => ({ ...f, color: v || undefined, page: 1 }))}
          placeholder="전체 색상"
          // 2026-10-21 오너 — 필터 줄에서 폭이 0 까지 줄어 화살표만 남았다(이름이 안 보임). 다른 고르기만큼 둔다.
          style={{ height: 32, minWidth: 120 }}
        />
      </div>

      {/* 2026-10-21 오너 — 화면 폭과 상관없이 두 줄로 고정: 윗줄 = 검색 · 고르기(필터),
          아랫줄 = 정렬 · 구분 사양 · 삭제됨 포함 · (오른쪽) 총 대수 · 기준일 · 지금 마감 · 내보내기 · 새 로봇. */}
      <div style={toolbar}>
        {/* S2 Phase 2.7 — 정렬 (출고일정/모델/상태/최신). 2026-10-21 오너 — 구분 사양과 자리 바꿈. */}
        <select
          aria-label="정렬"
          value={filters.sort ?? 'created_desc'}
          onChange={(e) => {
            // 정렬을 새로 고르면 머리 정렬은 푼다(두 순서가 겹치지 않게).
            setColSort(null);
            setFilters((f) => ({ ...f, sort: e.target.value as RobotListSort, page: 1 }));
          }}
          style={filterInput}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {/* S2 Phase 2.7 — 구분 사양 facet. 모델별 축이 달라 자유 텍스트
            exact-match (예: Right / 485 / 갈비). */}
        <input
          placeholder="구분 사양"
          aria-label="구분 사양 필터"
          value={filters.variantValue ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, variantValue: e.target.value || undefined, page: 1 }))
          }
          style={{ ...filterInput, width: 120 }}
        />
        {/* round6 §1-8 — 삭제된 로봇 표시 토글(복구/완전삭제 진입점). */}
        {canCreate && (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              color: 'var(--fg-muted)',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={!!filters.includeDeleted}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  includeDeleted: e.target.checked || undefined,
                  page: 1,
                }))
              }
            />
            삭제됨 포함
          </label>
        )}
        <div
          className="kuun-caption"
          style={{
            marginLeft: 'auto',
            whiteSpace: 'nowrap',
            color: isFetching ? 'var(--brand)' : 'var(--fg-muted)',
          }}
        >
          {isFetching ? '불러오는 중…' : `총 ${total.toLocaleString()} 대`}
        </div>
        {/* 2026-10-21 오너 — 기준일(마감본) 고르기도 '지금 마감' 옆으로. */}
        <select
          aria-label="기준일"
          data-testid="robots-asof-select"
          value={asOf ?? ''}
          onChange={(e) => selectAsOf(e.target.value || undefined)}
          style={asOf ? { ...filterInput, ...asOfSelectActive } : filterInput}
        >
          <option value="">기준일: 지금</option>
          {asOf && !snapshots.some((s) => s.basisDate === asOf) && (
            <option value={asOf}>{asOf}, 마감본 없음</option>
          )}
          {snapshots.map((s) => (
            <option key={s.id} value={s.basisDate}>
              {asOfOptionLabel(s)}
            </option>
          ))}
        </select>
        {/* 2026-10-21 오너 — '지금 마감'은 고르기(필터) 사이가 아니라 오른쪽 동작 버튼 자리에. */}
        {isAdmin && !asOf && (
          <Button
            type="button"
            variant="secondary"
            data-testid="robots-capture-now"
            disabled={captureM.isPending || isLastDay}
            title={
              isLastDay
                ? '말일 마감본은 23:59에 자동으로 만들어집니다'
                : '지금 이 순간의 로봇 목록을 마감본으로 남깁니다. 남긴 마감본은 고칠 수 없습니다.'
            }
            onClick={() => {
              if (
                window.confirm(
                  '지금 이 순간의 로봇 목록을 마감본으로 남깁니다.\n마감본은 고치거나 지울 수 없습니다. 계속할까요?',
                )
              ) {
                captureM.mutate();
              }
            }}
          >
            {captureM.isPending ? '마감 중…' : '지금 마감'}
          </Button>
        )}
        <Button type="button" variant="secondary" onClick={handleExport} disabled={exporting}>
          {exporting ? '내보내는 중…' : 'CSV 내보내기'}
        </Button>
        {/* 마감본은 회계 증빙이라 고칠 수 없다 — 등록·편집·삭제·복원 버튼을 그리지 않는다. */}
        {canCreate && !asOf && (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            + 새 로봇
          </Button>
        )}
      </div>

      <RobotCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {notesFor && (
        <Modal open onClose={() => setNotesFor(null)} title={`${notesFor.label} 비고`}>
          <div
            data-testid="robot-notes-full"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}
          >
            {notesFor.notes}
          </div>
        </Modal>
      )}
      {editTarget && <RobotEditModal open robot={editTarget} onClose={() => setEditTarget(null)} />}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-surface)',
          overflow: 'auto',
        }}
      >
        {error ? (
          <div style={emptyState}>
            <div style={{ color: 'var(--fg-danger)' }}>{extractApiError(error)}</div>
          </div>
        ) : isLoading ? (
          <TableSkeleton rows={8} columns={isAdmin ? 14 : 13} />
        ) : items.length === 0 ? (
          <div style={emptyState} className="kuun-dot-grid">
            <div className="kuun-h4">조건에 맞는 로봇이 없습니다</div>
            <div className="kuun-caption" style={{ marginTop: 4 }}>
              필터를 바꾸거나 새 로봇을 등록해 주세요.
            </div>
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                {/* §② — 좌측 3px 상태 컬러바 컬럼 (헤더 없음). */}
                <th style={{ ...th, width: 3, padding: 0 }} aria-hidden />
                {sortableTh('sn', 'SN')}
                {sortableTh('model', '모델')}
                {/* 개선계획 §4 — 한 칸에 예정/실적이 섞이므로 헤더에 범례를 단다. */}
                <th style={th}>
                  <Tooltip content={SHIPMENT_LEGEND}>출고일정</Tooltip>
                </th>
                {/* FB §2 — 색상 컬럼 (출고일정과 구분 사양 사이, 표시 전용). */}
                {sortableTh('color', '색상')}
                {sortableTh('variant', '구분 사양')}
                {sortableTh('status', '상태')}
                {sortableTh('location', '위치')}
                {/* windows-ux-edit Round 2 Phase B (슬라이드3) — 첫 제조/출고 월·현재
                    고객사·비고 추가, 회계 번호·현재 매장·검증 컬럼 제거. */}
                {sortableTh('firstProductionYm', '첫 제조 월')}
                {sortableTh('firstShipmentYm', '첫 출고 월')}
                {/* round10 (요구 4) — 구독(렌탈)/판매 구분. 첫 출고 월과 현재 고객사 사이. */}
                <th style={th}>구분</th>
                {sortableTh('customer', '현재 고객사')}
                {/* round11 요구3 — 회계번호(관리자 한정). 현재 고객사 ↔ 비고 사이. */}
                {isAdmin && sortableTh('accountingBookNo', '회계번호')}
                <th style={{ ...th, whiteSpace: 'nowrap' }}>비고</th>
                <th style={{ ...th, width: 80, textAlign: 'right' }}>액션</th>
              </tr>
            </thead>
            <tbody>
              {items.map((robot) => {
                const sn = robot.serialShort
                  ? formatRobotShortId(robot.serialShort)
                  : (robot.serialLong ?? '–');
                return (
                  <tr
                    key={robot.id}
                    className="kuun-row-hover"
                    style={{ borderTop: '1px solid var(--border-subtle)' }}
                  >
                    <td style={{ padding: 0, width: 3 }}>
                      <StatusBar
                        kind="robot"
                        status={robot.status}
                        height={36}
                        width={3}
                        aria-label={`status-${robot.status}`}
                      />
                    </td>
                    {/* round5 #7a — SN 은 행의 1차 식별자라 bold 로 강조. */}
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                      <Link
                        to={`/robots/${robot.id}`}
                        onClick={() => {
                          if (asOf) {
                            toast.info(
                              '상세 화면은 오늘 기준입니다. 마감 뒤에 바뀐 내용이 보일 수 있습니다.',
                              '지금의 로봇 상세로 이동합니다',
                            );
                          }
                        }}
                        style={{
                          color: 'var(--fg-brand)',
                          opacity: robot.deletedAt ? 0.55 : 1,
                          textDecoration: robot.deletedAt ? 'line-through' : undefined,
                        }}
                      >
                        {sn}
                      </Link>
                      {/* 개선계획 §2 (결정 ③A) — 고객 출고 이력이 없는 로봇. */}
                      {robot.isNew && (
                        <span style={{ marginLeft: 6 }}>
                          <NewBadge />
                        </span>
                      )}
                      {/* round6 §1-8 — 삭제됨 배지(includeDeleted 토글 시 노출). */}
                      {robot.deletedAt && (
                        <span
                          title={`삭제됨: ${robot.deletedAt.slice(0, 10)}`}
                          style={{
                            marginLeft: 6,
                            padding: '1px 6px',
                            borderRadius: 999,
                            border: '1px solid var(--border-strong)',
                            background: 'var(--bg-sunken)',
                            color: 'var(--fg-muted)',
                            fontSize: 10,
                            fontWeight: 600,
                            fontFamily: 'var(--font-sans)',
                          }}
                        >
                          삭제됨
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      <button
                        type="button"
                        title={`이 모델로 필터: ${robot.model.displayName}`}
                        aria-pressed={filters.modelCode === robot.model.code}
                        onClick={() =>
                          setFilters((f) => ({
                            ...f,
                            modelCode:
                              f.modelCode === robot.model.code ? undefined : robot.model.code,
                            page: 1,
                          }))
                        }
                        style={filterCellStyle(filters.modelCode === robot.model.code)}
                      >
                        {robot.model.displayName}
                      </button>
                    </td>
                    {/* 추가수정(26-07-27) §3 (결정 ①B) — 나가 있으면 실적(현재
                        배치 출고일, 중립색), 아직 안 나갔고 예정일이 있으면
                        예정(파랑) 한 칸. 고아 DEPLOYED 는 줄표 고정. */}
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                      <ShipmentScheduleCell robot={robot} />
                    </td>
                    {/* FB §2 — 색상 (표시 전용, 미상은 em-dash). */}
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{robot.color ?? '–'}</td>
                    {/* S2 Phase 2.7 — 구분 사양 (Model.variantAxisLabel: variantValue). */}
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {formatVariant(robot.model.variantAxisLabel, robot.variantValue)}
                    </td>
                    <td style={td}>
                      <RobotStatusPill status={robot.status} />
                    </td>
                    <td style={td}>
                      <LocationPill category={robot.locationCategory} />
                    </td>
                    {/* windows-ux-edit Round 2 Phase B — 첫 제조/출고 월(YY/MM),
                        현재 고객사(배치 매장 고객사 ?? 예약 고객사), 비고. */}
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                      {formatShipmentYm(robot.firstProductionYm)}
                    </td>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                      {formatShipmentYm(robot.firstShipmentYm)}
                    </td>
                    {/* round10 (요구 4) — 구독/판매 구분(예약계약 라인 reason 파생).
                        판매구분 정정(26-07-28) — 모델 매칭 파생 + 판정 불가는 '확인 필요'.
                        정정9(26-07-29) — 오너 지시로 구독/판매 두 단어만(서버가 접어 내려준다). */}
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <ContractKindCell robot={robot} />
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {robot.currentStore?.customerName ??
                        robot.reservedContract?.customerDisplayName ??
                        '–'}
                    </td>
                    {/* round11 요구3 — 회계번호(관리자 한정). 자산 대장 대조용이라
                        SN 처럼 mono 로 정렬해 보이게 한다. */}
                    {isAdmin && (
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                        {robot.accountingBookNo ?? '–'}
                      </td>
                    )}
                    {/* 2026-10-21 오너 — 긴 비고가 칸을 세로로 늘려 표가 깨졌다. 한 줄로 자르고('…'),
                        누르면 전체를 창으로 본다. */}
                    <td style={{ ...td, maxWidth: 220 }}>
                      {robot.notes ? (
                        <button
                          type="button"
                          data-testid={`robot-notes-${robot.id}`}
                          title="눌러서 비고 전체 보기"
                          onClick={() =>
                            setNotesFor({
                              label: robot.serialShort
                                ? formatRobotShortId(robot.serialShort)
                                : (robot.serialLong ?? '로봇'),
                              notes: robot.notes!,
                            })
                          }
                          style={notesBtn}
                        >
                          {robot.notes}
                        </button>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {/* round6 §1-8 — 삭제된 행은 복구/완전삭제 액션으로 대체.
                          기준일 마감본에서는 아무 동작도 두지 않는다(고칠 수 없는 기록). */}
                      {asOf ? null : robot.deletedAt ? (
                        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          {/* round12 결정4 — 제조팀에 개방한 건 소프트 삭제(휴지통)뿐이다.
                              복구/완전삭제는 ADMIN 유지(서버도 같은 경계). */}
                          {userRole === UserRole.ADMIN && (
                            <button
                              type="button"
                              onClick={() => restoreM.mutate(robot.id)}
                              disabled={restoreM.isPending}
                              data-testid={`robot-action-restore-${robot.id}`}
                              style={textActionBtn}
                            >
                              복구
                            </button>
                          )}
                          {userRole === UserRole.ADMIN && (
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `${sn} 로봇을 완전 삭제할까요?\n\n서비스 오더, 인수증, 서명 같은 고객사 데이터와 연결돼 있으면 삭제되지 않습니다. 삭제한 뒤에는 되돌릴 수 없습니다.`,
                                  )
                                ) {
                                  permDeleteM.mutate({ id: robot.id });
                                }
                              }}
                              disabled={permDeleteM.isPending}
                              data-testid={`robot-action-permanent-${robot.id}`}
                              style={{ ...textActionBtn, color: 'var(--fg-danger)' }}
                            >
                              완전 삭제
                            </button>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          {/* round5 #7c — eye(상세 보기)는 SN 링크와 완전 중복이라
                            인라인 '편집' 으로 교체. 목록 응답이 상세와 동일한
                            RobotSummaryDto 라 RobotEditModal 을 행 그대로 재사용. */}
                          {canCreate && (
                            <button
                              type="button"
                              aria-label="편집"
                              title="편집"
                              onClick={() => setEditTarget(robot)}
                              data-testid={`robot-action-edit-${robot.id}`}
                              style={iconBtn}
                            >
                              {/* pencil icon */}
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                              >
                                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                <path d="m15 5 4 4" />
                              </svg>
                            </button>
                          )}
                          <button
                            type="button"
                            aria-label="QR 보기"
                            title="QR 코드"
                            onClick={() => navigate(`/robots/${robot.id}?tab=qr`)}
                            data-testid={`robot-action-qr-${robot.id}`}
                            style={iconBtn}
                          >
                            {/* qr icon (simplified) */}
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <rect x="3" y="3" width="7" height="7" />
                              <rect x="14" y="3" width="7" height="7" />
                              <rect x="3" y="14" width="7" height="7" />
                              <path d="M14 14h3v3h-3zM20 14h1v1h-1zM14 20h1v1h-1zM20 20h1v1h-1z" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* docs/work_log — shared Pagination component (page-size selector,
          first/last/prev/next, summary). Replaces the inline pager that
          was identical across multiple list pages. */}
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={(p) => setFilters((f) => ({ ...f, page: p }))}
        onPageSizeChange={(s) => setFilters((f) => ({ ...f, page: 1, pageSize: s }))}
      />
    </div>
  );
}

const toolbar: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  // 2026-10-21 오너 — 필터가 늘어 화면 오른쪽으로 튀어나갔다(+ 새 로봇이 잘리고 '총 N 대'가 세로로 접힘).
  // 넘치면 다음 줄로 내린다.
  flexWrap: 'wrap',
};

const filterInput: CSSProperties = {
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
  color: 'var(--fg-default)',
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const td: CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'middle',
};

const notesBtn: CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
};

const emptyState: CSSProperties = {
  height: '100%',
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--fg-muted)',
  padding: 32,
};

// round7 §1-4 — 복구/완전 삭제 텍스트 액션 버튼(iconBtn 의 26×26 고정 박스를
// 상속하면 한글 라벨이 줄바꿈돼 박스를 벗어나던 문제 수정).
/** 기준일 마감본 안내 띠 — 지금 화면과 헷갈리지 않게 경고색. */
const asOfBanner: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 14px',
  borderRadius: 'var(--r-card)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-default)',
  fontSize: 13,
};
// 테두리는 줄임형(border)으로 덮는다 — 기본 스타일도 줄임형이라, 긴 이름(borderColor)으로 덮으면 '지금'으로
// 돌아갈 때 React 가 긴 이름만 지워 진한 테두리가 남는다(외부 검토).
const asOfSelectActive: CSSProperties = {
  border: '1px solid var(--brand)',
  fontWeight: 600,
};

const textActionBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 26,
  padding: '0 10px',
  whiteSpace: 'nowrap',
  fontSize: 12,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
};

const iconBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  padding: 0,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
};
